<section class="min-h-screen">
  <!-- Header -->
  <div class="mb-6">
    <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">Application Forms</h1>
    <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Configure and manage shareable application forms per institution and product</p>
  </div>

  <!-- Actions Row -->
  <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
    <div class="flex-1 min-w-[260px] max-w-xs">
      <form [formGroup]="searchQuery">
        <input type="text" formControlName="keyword" placeholder="Filter by institution or product..."
          class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 focus:border-primary-800 transition-colors" />
      </form>
    </div>
    <div class="flex items-center gap-2">
      <button (click)="loadForms()"
        class="px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 transition-colors flex items-center gap-1.5 text-xs font-semibold" title="Refresh">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Refresh</span>
      </button>
      <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button type="button" (click)="viewMode = 'table'" [class]="viewMode === 'table' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400'" title="Table view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
        </button>
        <button type="button" (click)="viewMode = 'grid'" [class]="viewMode === 'grid' ? 'p-1.5 bg-primary-800 text-white' : 'p-1.5 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-400'" title="Grid view">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/></svg>
        </button>
      </div>
      @if (permissionService.canManage('applicationForms')) {
      <button (click)="navigateToNew()"
        class="flex items-center gap-2 px-3 py-1.5 bg-primary-800 text-white hover:bg-primary-700 rounded-lg text-xs font-semibold transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
        </svg>
        New Form
      </button>
      }
    </div>
  </div>

  <!-- Table / Grid Toggle -->
  @if (viewMode === 'table') {
  <!-- Table -->
  <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
    <div class="overflow-x-auto">
      <div class="min-w-[800px]">
        <!-- Table Header -->
        <div class="grid grid-cols-6 gap-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 px-5 py-2.5">
          <div><p class="text-xs font-semibold text-slate-400">Institution</p></div>
          <div><p class="text-xs font-semibold text-slate-400">Product</p></div>
          <div class="text-left"><p class="text-xs font-semibold text-slate-400">Status</p></div>
          <div class="text-left"><p class="text-xs font-semibold text-slate-400">Created</p></div>
          <div class="col-span-2 text-center"><p class="text-xs font-semibold text-slate-400">Actions</p></div>
        </div>

        <!-- Table Body -->
        <div class="min-h-[240px]">
          @if (isLoading()) {
            <div class="flex items-center justify-center py-16">
              <app-progress-loader></app-progress-loader>
            </div>
          } @else if (forms().length) {
            @for (form of forms(); track form.id) {
              <div class="grid grid-cols-6 gap-2 px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                <div class="flex items-center">
                  <p class="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{{ form.institutionName || form.institutionId }}</p>
                </div>
                <div class="flex items-center">
                  <p class="text-xs text-slate-600 dark:text-slate-400 truncate">{{ form.productName || form.productId }}</p>
                </div>
                <div class="flex items-center justify-start">
                  <span class="px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-widest"
                    [class]="form.isActive
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900'
                      : 'bg-slate-50 dark:bg-slate-800/60 text-slate-400 border-slate-200 dark:border-slate-700'">
                    {{ form.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <div class="flex items-center justify-start">
                  <p class="text-xs text-slate-500 dark:text-slate-400">{{ form.createdAt | date:'dd MMM yyyy' }}</p>
                </div>
                <div class="col-span-2 flex items-center justify-center gap-2">
                  <button (click)="copyLink(form.token)"
                    class="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                    Copy Link
                  </button>
                  @if (permissionService.canManage('applicationForms')) {
                  <button (click)="navigateToEdit(form.id)"
                    class="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                    Edit
                  </button>
                  <button (click)="toggleActive(form)"
                    class="px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors"
                    [class]="form.isActive
                      ? 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                      : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'">
                    {{ form.isActive ? 'Deactivate' : 'Activate' }}
                  </button>
                  <button (click)="promptDelete(form)" [disabled]="deletingFormId() === form.id"
                    class="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-40 transition-colors">
                    {{ deletingFormId() === form.id ? '...' : 'Delete' }}
                  </button>
                  }
                </div>
              </div>
            }
          } @else {
            <div class="flex flex-col items-center justify-center py-16 text-center">
              <p class="text-sm font-bold text-slate-800 dark:text-slate-100">No application forms yet</p>
              <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Create your first form to start collecting applications.</p>
              @if (permissionService.canManage('applicationForms')) {
              <button (click)="navigateToNew()"
                class="mt-4 flex items-center gap-2 px-3 py-1.5 bg-primary-800 text-white hover:bg-primary-700 rounded-lg text-xs font-semibold transition-colors">
                New Form
              </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
  } @else {
  <!-- Grid View -->
  <div class="min-h-[240px]">
    @if (isLoading()) {
    <div class="flex items-center justify-center py-16"><app-progress-loader></app-progress-loader></div>
    } @else if (forms().length) {
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      @for (form of forms(); track form.id) {
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:shadow-md transition-shadow">
        <div class="flex items-start justify-between mb-3">
          <div class="min-w-0">
            <p class="text-sm font-bold text-slate-900 dark:text-white truncate">{{ form.institutionName || form.institutionId }}</p>
            <p class="text-xs text-slate-400 truncate mt-0.5">{{ form.productName || form.productId }}</p>
          </div>
          <span class="ml-2 shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-widest"
            [class]="form.isActive
              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900'
              : 'bg-slate-50 dark:bg-slate-800/60 text-slate-400 border-slate-200 dark:border-slate-700'">
            {{ form.isActive ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <div class="pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-between">
          <p class="text-[11px] text-slate-400">{{ form.createdAt | date:'dd MMM yyyy' }}</p>
          <div class="flex items-center gap-1.5">
            <button (click)="copyLink(form.token)" class="text-[10px] font-semibold px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
              Copy Link
            </button>
            @if (permissionService.canManage('applicationForms')) {
            <button (click)="navigateToEdit(form.id)" class="p-1.5 rounded text-slate-400 hover:text-primary-800 dark:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors" title="Edit">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
            </button>
            <button (click)="promptDelete(form)" [disabled]="deletingFormId() === form.id" class="p-1.5 rounded text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-colors disabled:opacity-40" title="Delete">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
            }
          </div>
        </div>
      </div>
      }
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center py-16 text-center">
      <p class="text-sm font-bold text-slate-800 dark:text-slate-100">No application forms yet</p>
    </div>
    }
  </div>
  }

  <!-- Pagination -->
  @if (!isLoading() && forms().length > 0) {
    <app-pagination
      [totalItems]="formsResponse()?.meta?.total || 0"
      [itemsPerPage]="queryParams().perPage || 10"
      [currentPage]="queryParams().page || 1"
      (pageChange)="onPageChange($event)">
    </app-pagination>
  }
</section>

<!-- Delete Confirmation Dialog -->
@if (formToDelete(); as form) {
<app-confirmation-dialog
  [title]="'Delete Application Form'"
  [message]="'Are you sure you want to delete this application form? This action cannot be undone.'"
  [itemName]="formLabel(form)"
  [confirmText]="'Delete'"
  [cancelText]="'Cancel'"
  [type]="'danger'"
  (onConfirm)="confirmDelete()"
  (onCancel)="cancelDelete()">
</app-confirmation-dialog>
}
